import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, SpinningLoader } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { usePayerNameContextFromSingleOrderLayout } from "../../../context/payerNameContextFromSingleOrderLayout";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import PdfRender from "../../pdfRender/pdfRender";

interface DocViewerModalProps {
  fileUrl?: string;
  onClose: () => void;
  type: "clinical_attachment" | "cmm_screenshot";
  shouldLogEvent?: boolean;
  screenName?: string;
  formName?: string;
}

const DocViewerModal = ({
  fileUrl,
  onClose,
  type,
  shouldLogEvent,
  screenName,
  formName,
}: DocViewerModalProps) => {
  const { metaData } = useSelector((state: RootState) => state.modal);
  const [currentFileUrl, setCurrentFileUrl] = useState<string>(fileUrl || "");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { singleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgID = getOrgIdForFetchExternalWorklist();
  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const isEventLogged = useRef(false);

  useEffect(() => {
    if (shouldLogEvent && !isEventLogged.current) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: EventsName.MODAL_SHOWN,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          model_name:
            type === "clinical_attachment" ? "Clinical Document" : "Screenshot",
          screen_name: ScreenNamesForBigQuery.FORM_SCREEN,
          payer_name: payerName,
        },
      });
      isEventLogged.current = true;
    }
  }, []);

  useEffect(() => {
    const fetchFileUrl = async () => {
      if (!metaData?.filePath) return;

      setIsLoading(true);
      try {
        const downloadUrl = await getFileDownloadUrl(metaData.filePath);
        if (downloadUrl) {
          setCurrentFileUrl(downloadUrl);
        }
      } catch (error) {
        console.error("Error fetching file URL:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (metaData?.filePath) {
      setIsLoading(true);
      fetchFileUrl();
    } else if (fileUrl) {
      setCurrentFileUrl(fileUrl);
      setIsLoading(false);
    }
  }, [metaData?.filePath, fileUrl]);

  const handleDownload = () => {
    if (currentFileUrl) {
      window.open(currentFileUrl, "_blank");
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgID ?? "",
        event_name: EventsName.DOWNLOAD_BUTTON_CLICKED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: screenName ?? "",
          form_name: formName ?? "",
          payer_name: payerName,
        },
      });
      onClose();
    }
  };

  return (
    <Modal
      dialogId={"doc-viewer-modal"}
      onSave={handleDownload}
      title={
        metaData?.title
          ? metaData?.title
          : type === "clinical_attachment"
            ? "Clinical Document"
            : "Screenshot"
      }
      saveButtonText={"Download"}
      cancelText={"Cancel"}
      heightPercentage={90}
      onCancel={onClose}
      onClose={onClose}
      hideFooter={metaData?.hideFooter ?? type === "cmm_screenshot"}
    >
      <div className="doc-viewer-modal--container h-full w-full overflow-scroll">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center gap-4">
            <SpinningLoader />
            <p className="text-gray-600">Loading document...</p>
          </div>
        ) : currentFileUrl ? (
          <PdfRender file={currentFileUrl} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <p className="text-gray-600">No document available</p>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocViewerModal;
